import type { FuelSurchargeLookupRow } from '../types';
import { inferFuelVehicleTypeFromText } from './fuelSurchargeDefault';

export type { FuelSurchargeLookupRow };

/** UTC midnight of an ISO `yyyy-mm-dd` — same as server `workDateFromIso`. */
export function workDateFromIso(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  if (year && month && day) {
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  }
  return new Date();
}

/** A valid per-line $/hr override: a finite, non-negative number. */
function isValidFuelOverride(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * Latest fuels-table surcharge whose `effectiveAt` is on or before the work date.
 * On-Road / Off-Road map to the two table types; N/A or unknown → 0.
 */
export function lookupFuelSurchargePerHour(
  history: FuelSurchargeLookupRow[],
  vehicleType: string | null | undefined,
  date: string,
): number {
  const type = inferFuelVehicleTypeFromText(vehicleType);
  if (!type) return 0;
  const asOf = workDateFromIso(date).getTime();
  let best: FuelSurchargeLookupRow | null = null;
  for (const row of history) {
    if (row.vehicleType !== type) continue;
    const effective = new Date(row.effectiveAt).getTime();
    if (Number.isNaN(effective) || effective > asOf) continue;
    if (!best) {
      best = row;
      continue;
    }
    const bestAt = new Date(best.effectiveAt).getTime();
    if (effective > bestAt || (effective === bestAt && row.id > best.id)) {
      best = row;
    }
  }
  return best?.surchargePerHour ?? 0;
}

/** Applies a per-line $/hr override when set; otherwise the fuels-table lookup. */
export function fuelSurchargePerHourForLine(
  history: FuelSurchargeLookupRow[],
  row: {
    fuelSurchargeEligible?: boolean | null;
    vehicleType?: string | null;
    date: string;
    fuelSurchargeOverridePerHour?: number | null;
  },
): number {
  if (row.fuelSurchargeEligible !== true) return 0;
  if (isValidFuelOverride(row.fuelSurchargeOverridePerHour)) {
    return row.fuelSurchargeOverridePerHour;
  }
  return lookupFuelSurchargePerHour(history, row.vehicleType, row.date);
}
